import { addDays, format } from 'date-fns'
import { clasificarTemporal } from '@/core/components/tareas'
import { formatDate } from '@/lib/utils'
import type { TareaCompleta } from '@/types/database'
import type { VencimientoConCliente } from '@/types/vencimientos'

export interface TareasPanel {
  urgentes: TareaCompleta[]
  vencidas: TareaCompleta[]
  deHoy: TareaCompleta[]
  estaSemana: TareaCompleta[]
}

/**
 * Agrupa las tareas para el panel de inicio. Las urgentes (no completadas) van a su
 * propio grupo resaltado sin importar la fecha; el resto se clasifica por fecha_limite
 * en vencidas / hoy / esta semana. (feedback Lisandro: el asistente solo veía vencidas/hoy
 * y se perdía sus tareas próximas; las urgentes ahora se resaltan.)
 */
export function agruparTareasPanel(tareas: TareaCompleta[]): TareasPanel {
  const urgentes: TareaCompleta[] = []
  const vencidas: TareaCompleta[] = []
  const deHoy: TareaCompleta[] = []
  const estaSemana: TareaCompleta[] = []

  tareas.forEach((tarea) => {
    if (tarea.estado === 'completada') return
    if (tarea.prioridad === 'urgente') {
      urgentes.push(tarea)
      return
    }
    const clasificacion = clasificarTemporal(tarea, { priorizarUrgente: false })
    if (clasificacion === 'vencida') vencidas.push(tarea)
    else if (clasificacion === 'hoy') deHoy.push(tarea)
    else if (clasificacion === 'esta_semana') estaSemana.push(tarea)
  })

  // Oldest overdue first
  vencidas.sort((a, b) => (a.fecha_limite ?? '').localeCompare(b.fecha_limite ?? ''))

  return { urgentes, vencidas, deHoy, estaSemana }
}

/**
 * Hides already-resolved vencimientos (the hook loads them on purpose, ORR-73)
 * and past-due ones: the service also returns ALL historical 'vencido' rows
 * regardless of the day window, but this card is strictly "upcoming".
 */
export function filtrarVencimientosPanel(
  vencimientos: VencimientoConCliente[]
): VencimientoConCliente[] {
  const hoy = format(new Date(), 'yyyy-MM-dd')
  return vencimientos.filter(
    (v) =>
      v.estado !== 'presentado' &&
      v.estado !== 'pagado' &&
      v.fecha_vencimiento != null &&
      v.fecha_vencimiento >= hoy
  )
}

/**
 * "Hoy" / "Mañana" / dd/mm/aaaa for the panel list.
 * Compara fecha_vencimiento contra el día LOCAL del cliente (igual que
 * filtrarVencimientosPanel) en vez de `dias_para_vencer`, que la DB calcula
 * en UTC y cerca de medianoche AR (UTC-3) se corre un día (ORR-139 follow-up).
 */
export function etiquetaVencimiento(vencimiento: VencimientoConCliente): string {
  if (vencimiento.fecha_vencimiento == null) return '—'
  const hoy = format(new Date(), 'yyyy-MM-dd')
  const manana = format(addDays(new Date(), 1), 'yyyy-MM-dd')
  if (vencimiento.fecha_vencimiento === hoy) return 'Hoy'
  if (vencimiento.fecha_vencimiento === manana) return 'Mañana'
  return formatDate(vencimiento.fecha_vencimiento)
}
